import Checkbox from 'app/components/checkbox'
import FullPageDialog from 'app/components/full-page-dialog'
import { NameValueColumn } from 'app/components/name-value-col'
import TextInput from 'app/components/text-input'
import { Copy2Clipboard } from 'app/modules/reports/components'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { AssetsService } from 'app/modules/shared/services'
import React, { ChangeEvent, MouseEvent, useEffect, useMemo, useState } from 'react'
import SyntaxHighlighter from 'react-syntax-highlighter/dist/esm/default-highlight'
import { atomOneLight } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import { YaraRuleExtendedData } from '../../yara-report/types'
import { FilterValues } from './filter-values'
import DropdownSelect from 'app/components/dropdown'

const YaraSearch = ({
  onApply,
  searchRules,
  filterId,
  value,
}: {
  onApply: (rules: string[], ...args: any[]) => void
  searchRules: string[]
  filterId?: string
  value?: string
}) => {
  const [isOpen, setOpen] = useState(false)
  const [allRules, setAllRules] = useState<YaraRuleExtendedData[]>([])
  const [filteredRules, setFilteredRules] = useState<YaraRuleExtendedData[]>([])
  const [selectedRules, setSelectedRules] = useState<string[]>(searchRules)
  const [previewRule, setPreviewRule] = useState<YaraRuleExtendedData | null>(null)
  const [allSelected, setAllSelected] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [authorFilter, setAuthorFilter] = useState('')
  const [authors, setAuthors] = useState<string[]>([])
  const [triggerSubmit, setTriggerSubmit] = useState(false)
  const [enableSubmit, setEnableSubmit] = useState(false)
  const filterValues = value ? value.split(',') : []
  // TODO TBA later
  // const [searchNamesOnly, setSearchNamesOnly] = useState(true)

  const { _t } = useTypedTranslation()

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget as HTMLInputElement
    setSearchInput(value)
  }

  const handlePreview = (rule: YaraRuleExtendedData) => {
    setPreviewRule(rule.name === previewRule?.name ? null : rule)
  }

  const handleSelectAll = () => {
    const isAllSelectedNow = !allSelected
    setSelectedRules(isAllSelectedNow ? filteredRules.map(({ name }) => name) : [])
    setAllSelected(isAllSelectedNow)
  }

  const handleSelectRule = (rule: string) => {
    const newSelectedList = selectedRules.includes(rule)
      ? selectedRules.filter((_rule) => _rule !== rule)
      : [...selectedRules, rule]

    setSelectedRules([...newSelectedList])
    setAllSelected(newSelectedList.length === filteredRules.length)

    // Enable to save cleared filter
    setEnableSubmit(!!searchRules.length)
  }

  const handleClearSelection = () => {
    setSelectedRules([])
    setAllSelected(false)
    // Enable to save cleared filter
    setEnableSubmit(!!searchRules.length)
  }

  const handleRemoveRule = (rule: string) => handleSelectRule(rule)

  const handleRemoveFromInput = (rule: string, e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    handleRemoveRule(rule)
    setTriggerSubmit(true)
  }

  const handleSubmit = () => {
    onApply(selectedRules)
  }

  const filteredBySearch = useMemo(() => {
    if (!searchInput) {
      return allRules
    }

    const searchInputLower = searchInput.toLowerCase()

    // TODO condition check TBA later
    //if (searchNamesOnly) {
    return allRules.filter((rule) => rule.name.toLowerCase().includes(searchInputLower))
    //}
  }, [searchInput, allRules])

  useEffect(() => {
    const allAuthors = [...new Set(filteredBySearch.map(({ author = '' }) => author).filter(Boolean))]
    setAuthors(allAuthors)

    const filteredByAuthor = filteredBySearch.filter((rule) => (authorFilter ? rule.author === authorFilter : true))
    setFilteredRules(filteredByAuthor)

    setAllSelected(false)
  }, [authorFilter, filteredBySearch])

  useEffect(() => {
    if (triggerSubmit) {
      setTriggerSubmit(false)
      handleSubmit()
    }
  }, [triggerSubmit])

  useEffect(() => {
    if (!isOpen) return

    const fetchRules = async () => {
      const rules = await AssetsService.getYaraRules()
      const allAuthors = await AssetsService.getYaraAuthors()

      setAuthors(allAuthors)
      setAllRules(rules)
      setFilteredRules(rules)
    }

    fetchRules()
  }, [isOpen])

  useEffect(() => setSelectedRules(searchRules), [searchRules]) // Triggered if rules removed from threat hunting's bottom filter summary

  const YaraSearchContent = () =>
    isOpen ? (
      <div className="yara-search-container">
        <div className="yara-search-filter">
          <div className="d-block flex-grow-1" style={{ flexBasis: 0 }}>
            <p className="pb-1 m-0">{_t('author')}</p>
            <DropdownSelect
              id="author"
              searchable
              value={authorFilter}
              items={[{ value: '', name: _t('all') }, ...authors.map((author) => ({ value: author, name: author }))]}
              onClick={(value) => setAuthorFilter(value)}
            />
          </div>
          <div className="d-block flex-grow-1">
            <p className="pb-1 m-0">{_t('search')}</p>
            <TextInput
              id="yara-search"
              placeholder={_t('search-rule-details')}
              value={searchInput}
              onChange={handleSearchChange}
              prefix={<i className="ds3-icon ds3-search" />}
              //postfix={<i className="ds3 ds3-help" />} // Disabled helper until having helper text
              clearable
            />
          </div>
          <div className="flex-grow-1 d-block m-auto">
            {/*placeholder*/}

            {/* TODO to be added later */}
            {/* <p className="yara-search-cb-placeholder">placeholder</p>
          <Checkbox className="d-flex align-itmes-center" label={"Search rule names only"} onChange={() => setSearchNamesOnly(!searchNamesOnly)} checked={searchNamesOnly} /> */}
          </div>
        </div>
        <div className="yara-search-body">
          <div className="yara-rules-list">
            <div className="yara-rules-list-header">
              <Checkbox
                label={`${_t('yara-rules')} (${filteredRules.length})`}
                onChange={handleSelectAll}
                checked={allSelected}
              />
            </div>
            <div className="yara-rules-list-wrapper">
              {filteredRules &&
                filteredRules.map((rule) => (
                  <div
                    className={`yara-rules-list-element${rule.name === previewRule?.name ? ' selected' : ''}`}
                    key={rule.name}
                  >
                    <div className="yara-rule-cb">
                      <Checkbox
                        onChange={() => handleSelectRule(rule.name)}
                        checked={selectedRules.includes(rule.name)}
                      />
                    </div>
                    {/* Unused for now */}
                    {/* <div className="yara-rule-number text-m">
                  {}
                </div> */}
                    <span className="yara-rule-name" onClick={() => handlePreview(rule)}>
                      {rule.name}
                    </span>
                  </div>
                ))}
            </div>
          </div>
          <div className="yara-rule-content hide-mobile overflow-auto">
            {previewRule?.content ? (
              <div className="highlighted-code">
                <SyntaxHighlighter
                  style={atomOneLight}
                  language="C"
                  wrapLongLines
                  lineProps={{ style: { wordBreak: 'break-word' } }}
                >
                  {previewRule.content}
                </SyntaxHighlighter>
                <div className="copy-highlighted-code">
                  <Copy2Clipboard copyText={previewRule.content} />
                </div>
              </div>
            ) : null}
          </div>
          <div className="yara-rule-details hide-mobile">
            <div className="small-header mb-5 font-size-13 line-height-150">{_t('details')}</div>
            {previewRule?.author ? (
              <NameValueColumn
                name="author"
                value={previewRule?.author}
                className="mb-4"
                nameClass="font-size-13 mb-0"
              />
            ) : null}
            {previewRule?.description ? (
              <NameValueColumn
                name="description"
                value={previewRule?.description}
                className="mb-4"
                nameClass="font-size-13 mb-0"
              />
            ) : null}
            {Object.entries(previewRule?.other_data ?? {}).map(([name, value]) => {
              return (
                <NameValueColumn
                  key={name}
                  name={name}
                  value={value as string}
                  className="mb-4"
                  nameClass="font-size-13 mb-0"
                />
              )
            })}
          </div>
        </div>
      </div>
    ) : (
      <></>
    )

  return (
    <>
      <p className="text-note text-m m-0 pb-1">{_t('yara-rules')}</p>
      <div onClick={() => setOpen(true)}>
        <TextInput
          inputClassName="tags-filter-input"
          id={filterId}
          placeholder={`${_t('browse')} ${_t('yara-rules')}`}
          value={
            filterValues.length ? (
              <FilterValues values={filterValues} showAll={false} onRemove={handleRemoveFromInput} />
            ) : (
              ''
            )
          }
          postfix={<i className="ds3-icon ds3-external-link ds3-hover" />}
          cursor="pointer"
          noWrap
          readOnly
        />
      </div>
      {isOpen ? (
        <FullPageDialog
          body={YaraSearchContent()}
          title={_t('yara-rules')}
          description={_t('yara-rules-filter-description')}
          isOpen
          onClose={() => setOpen(false)}
          filteredList={selectedRules}
          contentLength={allRules.length}
          onClearSelection={handleClearSelection}
          onRemoveFilter={handleRemoveRule}
          onApply={handleSubmit}
          enableSubmit={enableSubmit}
          filterSummaryLabel={`${_t('yara-rules-selected')}:`}
          noFilterLabel={_t('no-yara-rules-selected')}
          applyButtonLabel={_t('apply')}
        />
      ) : null}
    </>
  )
}

export default YaraSearch
